import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion } from '@theme/utilities';
import { initAllBuyButtonsBlocks } from '@theme/buy-buttons-store-selector';
import {
  computeAvailabilityStateForElement,
  getSelectedAvailabilityBakeryId,
  getSelectedPickupDay,
} from '@theme/availability';
import { showThemedAlert } from '@theme/themed-alert';

/**
 * Guest quick-add → store selector handoff (BDH-2744).
 *
 * With no bakery selected, availability.js keeps the `+` visible on eComm
 * cards (card state `no_bakery`) instead of hiding it. A click on that `+`
 * must not hit `/cart/add.js` — there is no bakery to add against and the MF20
 * gate would only surface a confusing "Only 0 items" alert — so it is routed
 * into the store selector drawer first. Once the shopper SAVES a bakery and
 * the drawer has closed, the original intent is resumed:
 *
 *   - `add`    (single-variant, no MO06 modifiers): re-check the card's
 *              availability at the new bakery and submit the quick-add form,
 *              which opens the cart drawer through the normal `CartAddEvent`
 *              path. If the product isn't available there, explain why with a
 *              themed alert (the card badge has already re-rendered too).
 *   - `choose` (multi-variant / MO06): open the quick-add modal as the click
 *              would have — the morphed buy-buttons now see a selection and
 *              render the real ATC instead of "Select my bakery".
 *
 * Only ONE intent is held at a time (module-level), because there is one
 * drawer. Dismissing the drawer without saving drops the intent.
 *
 * Event-order gotcha: the `on:click` delegation in `@theme/component` is a
 * DOCUMENT-level CAPTURE listener, so it fires before any listener on
 * `<quick-add-component>`. That is why the `--choose` button is gated inside
 * `handleClick` itself, while the `--add` submit button is intercepted by a
 * capture listener on the component, which still runs early enough for
 * `preventDefault()` to cancel the native form submission.
 */
const STORE_SELECTOR_ROOT_ID = 'store-selector-drawer-root';
const SELECTION_CHANGED_EVENT = 'store-selection:changed';
const SELECTION_CLEARED_EVENT = 'store-selection:cleared';
// Mirrors `products.product.quick_add_unavailable_at_bakery`. Keep it free of
// apostrophes: the `| t:` filter HTML-escapes its output once it is given
// parameters, so an `'` in the locale string would reach the alert as `&#39;`.
const UNAVAILABLE_AFTER_SELECT_FALLBACK = '{{ product }} is not available at {{ bakery }} right now.';

/**
 * @typedef {'add' | 'choose'} GuestQuickAddMode
 * @typedef {{
 *   component: QuickAddComponent,
 *   mode: GuestQuickAddMode,
 *   selected: boolean,
 *   abort: AbortController,
 * }} PendingGuestQuickAdd
 */

/** @type {PendingGuestQuickAdd | null} */
let pendingGuestQuickAdd = null;

/**
 * @returns {(HTMLElement & { open: (opener?: HTMLElement | null) => void }) | null}
 */
function getStoreSelectorDrawer() {
  const el = document.getElementById(STORE_SELECTOR_ROOT_ID);
  if (!(el instanceof HTMLElement)) return null;
  if (typeof (/** @type {any} */ (el).open) !== 'function') return null;
  return /** @type {any} */ (el);
}

/** @param {HTMLElement} drawer */
function isStoreSelectorDrawerOpen(drawer) {
  const dialog = drawer.querySelector('dialog');
  return dialog instanceof HTMLDialogElement && dialog.open;
}

/**
 * Drop the held intent. When `owner` is given, only drop it if that component
 * armed it — a disconnecting card must not cancel another card's handoff.
 *
 * @param {QuickAddComponent} [owner]
 */
function disarmPendingGuestQuickAdd(owner) {
  if (!pendingGuestQuickAdd) return;
  if (owner && pendingGuestQuickAdd.component !== owner) return;
  pendingGuestQuickAdd.abort.abort();
  pendingGuestQuickAdd = null;
}

/**
 * Cached PDP HTML older than this is treated as stale. The flyout's cached
 * variant-picker markup carries variant ids + `available` flags; inventory can
 * deplete while the page sits open, so an unbounded cache risks submitting a
 * sold-out variant and tripping a Shopify stock error even when aggregate stock
 * exists. The cache is also cleared outright on every successful cart update
 * (see `#handleCartUpdate`) — the TTL just covers server-side drift with no
 * local cart activity.
 */
const PDP_CACHE_TTL_MS = 60000;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #cartUpdateAbort = null;
  /** @type {Map<string, { grid: Element, ts: number }>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productLink = this.#getProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Product surface wrapping quick-add: `product-card` (Bakers/default) or `product-tile` (COBS).
   * @returns {HTMLElement | null}
   */
  #getProductSurface() {
    return this.closest('product-card') ?? this.closest('product-tile');
  }

  /**
   * PDP link from the surrounding product card or COBS product tile.
   * @returns {HTMLAnchorElement | null}
   */
  #getProductLink() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    if (productCard) {
      return productCard.getProductCardLink();
    }

    const productTile = this.closest('product-tile');
    if (!productTile) return null;

    const tileLink = productTile.querySelector('a.product-tile__link');
    return tileLink instanceof HTMLAnchorElement ? tileLink : null;
  }

  /**
   * Gets the currently selected variant ID from the product card or tile
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const surface = this.#getProductSurface();
    if (!surface) return null;

    const checkedInput = /** @type {HTMLInputElement | null} */ (
      surface.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Inventory/availability can change after a product's PDP HTML is cached
    // (this shopper or another buying it). Drop the cache on every successful
    // cart update so the next flyout open re-fetches fresh variant state and
    // can't submit a stale/sold-out variant.
    this.#cartUpdateAbort = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbort.signal,
    });

    // Guest handoff for the direct-add `+` (see the module doc above). Capture
    // phase so `preventDefault()` lands before the submit button's default
    // action; the `--choose` button is handled inside `handleClick`.
    this.addEventListener('click', this.#handleGuestAddClick, {
      capture: true,
      signal: this.#cartUpdateAbort.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cartUpdateAbort?.abort();

    // The PLP availability sort re-appends every grid item on a bakery change
    // (`availability-grid-sort.js` reorderGrid → fragment → appendChild), which
    // fires disconnected + connected synchronously for a card that never really
    // left the page. Defer the teardown the guest handoff depends on until we
    // know the node is actually gone — otherwise the very selection change the
    // handoff is waiting for would drop the pending intent, and the PDP fetch
    // for the `choose` resume would be aborted mid-flight (empty modal).
    queueMicrotask(() => {
      if (this.isConnected) return;
      this.#abortController?.abort();
      disarmPendingGuestQuickAdd(this);
    });
  }

  /** @param {CartUpdateEvent} event */
  #handleCartUpdate = (event) => {
    // Keep the cache after a failed add — nothing changed server-side.
    if (event?.detail?.data?.didError) return;
    this.#cachedContent.clear();
  };

  /**
   * True when a quick-add click should open the store selector instead of
   * acting: commerce is on, no bakery (with an MF01 id) is selected, and the
   * drawer exists on the page. In design mode the drawer isn't rendered and
   * availability.js keeps the guest `+` hidden, so this stays false there.
   */
  #shouldRouteToStoreSelector() {
    if (document.documentElement.dataset.commerceEnabled !== 'true') return false;
    if (getSelectedAvailabilityBakeryId()) return false;
    return getStoreSelectorDrawer() !== null;
  }

  /** @param {Event} event */
  #handleGuestAddClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.quick-add__button--add');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (!this.#shouldRouteToStoreSelector()) return;

    event.preventDefault();
    event.stopPropagation();
    this.#routeToStoreSelector(button, 'add');
  };

  /**
   * Open the store selector on the shopper's behalf and remember what to do
   * once they save a bakery.
   *
   * `store-selection:changed` fires synchronously inside the drawer's save
   * (`StoreSelectionState.setSelection`) BEFORE it starts closing, so it is
   * only trusted while the drawer is open — a `changed` arriving with the
   * drawer shut is some other writer (MF10 hydration, cross-tab storage
   * bridge) and drops the intent rather than adding on the next close. The
   * add itself is deferred to the drawer's `dialog:close` (dispatched after
   * its close animation) so the cart drawer never opens on top of a closing
   * dialog, and so the drawer's own `syncCartAttributes` — enqueued at save
   * time — is already ahead of the add in the cart mutation queue.
   *
   * @param {HTMLElement} opener - the clicked `+`, handed to the drawer so
   *   focus returns to it on close
   * @param {GuestQuickAddMode} mode
   */
  #routeToStoreSelector(opener, mode) {
    const drawer = getStoreSelectorDrawer();
    if (!drawer) return;

    disarmPendingGuestQuickAdd();

    const abort = new AbortController();
    /** @type {PendingGuestQuickAdd} */
    const pending = { component: this, mode, selected: false, abort };
    pendingGuestQuickAdd = pending;
    const { signal } = abort;

    window.addEventListener(
      SELECTION_CHANGED_EVENT,
      () => {
        if (isStoreSelectorDrawerOpen(drawer)) {
          pending.selected = true;
        } else {
          disarmPendingGuestQuickAdd(this);
        }
      },
      { signal }
    );
    window.addEventListener(SELECTION_CLEARED_EVENT, () => disarmPendingGuestQuickAdd(this), { signal });
    drawer.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        const { selected } = pending;
        disarmPendingGuestQuickAdd(this);
        if (!selected || !this.isConnected) return;
        void this.#resumeAfterBakerySelected(mode);
      },
      { signal }
    );

    drawer.open(opener);
  }

  /**
   * The shopper saved a bakery from the `+` handoff and the drawer has closed.
   * @param {GuestQuickAddMode} mode
   */
  async #resumeAfterBakerySelected(mode) {
    const bakeryId = getSelectedAvailabilityBakeryId();
    // A location without an MF01 internal id can't be checked against MF16 —
    // availability.js has re-hydrated the cards as guest state, so there is
    // nothing sensible to add and the `+` will simply route again on click.
    if (!bakeryId) return;

    if (mode === 'choose') {
      const choosingButton = /** @type {HTMLButtonElement | null} */ (
        this.querySelector('.quick-add__button--choose')
      );
      await this.#openChooseFlow(choosingButton);
      return;
    }

    const badge = this.#getAvailabilityBadge();
    const state = badge
      ? computeAvailabilityStateForElement(badge, bakeryId, getSelectedPickupDay())
      : 'available';

    if (state !== 'available') {
      void showThemedAlert(this.#unavailableAfterSelectMessage());
      return;
    }

    const form = this.querySelector('form');
    const submitButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.quick-add__button--add')
    );
    if (!(form instanceof HTMLFormElement) || !submitButton || submitButton.disabled) return;

    // `requestSubmit` fires a real `submit` event, so the add goes through
    // ProductFormComponent.handleSubmit exactly as a click would (MF20 gate,
    // cart mutation queue, CartAddEvent → cart drawer).
    form.requestSubmit(submitButton);
  }

  /**
   * Any availability badge inside the owning card carries the product's
   * MF16/MF20/MF34/MF36 attributes; the gallery renders a suppressed one right
   * next to this component precisely so quick-add always has one to read.
   * @returns {HTMLElement | null}
   */
  #getAvailabilityBadge() {
    const surface = this.#getProductSurface() ?? this;
    const badge = surface.querySelector('[data-availability-state]');
    return badge instanceof HTMLElement ? badge : null;
  }

  #unavailableAfterSelectMessage() {
    const template =
      /** @type {any} */ (window).Theme?.translations?.quick_add_unavailable_at_bakery ||
      UNAVAILABLE_AFTER_SELECT_FALLBACK;
    const stateApi = /** @type {any} */ (window).StoreSelectionState;
    const selection = typeof stateApi?.getSelection === 'function' ? stateApi.getSelection() : null;
    const bakery = String(selection?.name || '').trim() || 'your bakery';
    const product = String(this.dataset.productTitle || '').trim() || 'This product';
    return template.replace('{{ product }}', product).replace('{{ bakery }}', bakery);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    const isCommerceEnabled = document.documentElement.dataset.commerceEnabled === 'true';
    if (!isCommerceEnabled) return;

    const choosingButton = /** @type {HTMLButtonElement|null} */ (
      event.target instanceof Element
        ? event.target.closest('.quick-add__button--choose')
        : this.querySelector('.quick-add__button--choose')
    );

    // Guest handoff (see module doc): pick a bakery first, then open the modal.
    if (this.#shouldRouteToStoreSelector()) {
      this.#routeToStoreSelector(choosingButton ?? this, 'choose');
      return;
    }

    await this.#openChooseFlow(choosingButton);
  };

  /**
   * Fetch (or reuse) the PDP markup, morph it into the shared quick-add modal
   * and open it. Split out of `handleClick` so the guest handoff can re-run it
   * after a bakery is saved.
   *
   * @param {HTMLButtonElement | null} choosingButton
   */
  async #openChooseFlow(choosingButton) {
    // Engage the loading state SYNCHRONOUSLY — before any awaits — so the
    // spinner replaces the choose-options icon on the same frame as the
    // click. The button uses the same `.add-to-cart-text` / `::after`
    // markup as the ATC button, so the shared `.atc-loading` CSS in
    // `snippets/add-to-cart-button.liquid` handles the visual swap
    // without new styles. On the first click for a product the
    // `await this.fetchProductPage(...)` below downloads the full PDP
    // HTML which can take 200–800ms on a cold cache — previously the
    // user got zero feedback during that window. Subsequent clicks hit
    // the in-memory cache so the spinner flashes off within a frame,
    // which is the expected "instant" feel.
    choosingButton?.classList.add('atc-loading');
    choosingButton?.setAttribute('aria-busy', 'true');

    try {
      const currentUrl = this.productPageUrl;

      // Use cached content only when it's still fresh (see PDP_CACHE_TTL_MS).
      const cached = this.#cachedContent.get(currentUrl);
      let productGrid =
        cached && Date.now() - cached.ts < PDP_CACHE_TTL_MS ? cached.grid : undefined;

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, { grid: productGrid, ts: Date.now() });
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
      }

      this.#openQuickAddModal();
    } finally {
      // Always unwind the loading state — covers cache miss, fetch
      // failure (fetchProductPage returns null), modal-update throw,
      // and the happy path. Modal-open is dialog-managed from here so
      // the user's next interaction surface is the modal itself, not
      // this button.
      choosingButton?.classList.remove('atc-loading');
      choosingButton?.removeAttribute('aria-busy');
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncBackgroundImageToModal(dialogComponent);

    dialogComponent.showDialog();
  };

  /**
   * Copies the product-card scoped background image var onto the shared quick-add modal.
   * @param {QuickAddDialog} dialogComponent
   */
  #syncBackgroundImageToModal(dialogComponent) {
    const backgroundImage = getComputedStyle(this).getPropertyValue('--quick-add-background-image').trim();
    /** @type {HTMLDialogElement | null} */
    const dialog = dialogComponent.querySelector('dialog');
    if (!dialog) return;

    dialog.style.setProperty('--quick-add-background-image', backgroundImage || 'none');
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#insertViewFullProductLink(modalContent);
    this.#cloneTitlePriceAndCaloriesIntoMedia(modalContent);

    // PDP buy-buttons rely on buy-buttons-store-selector.js (DOMContentLoaded + variant:update).
    // Morphed flyout content is inserted later, so run the same init here.
    initAllBuyButtonsBlocks();
  }

  /**
   * Calories block with rendered content (not the empty style-only wrapper).
   * @param {ParentNode | null | undefined} root
   * @returns {HTMLElement | null}
   */
  #findCaloriesBlock(root) {
    if (!root) return null;

    for (const block of root.querySelectorAll('.amb-calories')) {
      if (block instanceof HTMLElement && block.querySelector('.text-block')) {
        return block;
      }
    }

    return null;
  }

  /**
   * Clones product title, optional COBS calories, and price into `.product-information__media`.
   * @param {Element} modalContent
   */
  #cloneTitlePriceAndCaloriesIntoMedia(modalContent) {
    const media = modalContent.querySelector('.product-information__media');
    if (!media) return;

    media.querySelector('[data-quick-add-media-clone]')?.remove();

    const details = modalContent.querySelector('.product-details');
    /** @type {HTMLElement | null} */
    let titleBlock = null;
    /** @type {Element | null} */
    let priceEl = null;
    /** @type {HTMLElement | null} */
    let caloriesEl = null;

    if (details) {
      titleBlock = details.querySelector('.view-product-title');
      priceEl = details.querySelector('product-price');
      caloriesEl = this.#findCaloriesBlock(details);
    } else {
      const header = modalContent.querySelector('.product-header');
      priceEl = modalContent.querySelector('product-price');
      const link = header?.querySelector('a');
      if (link && priceEl) {
        const wrap = document.createElement('div');
        wrap.className = 'view-product-title';
        wrap.appendChild(link.cloneNode(true));
        titleBlock = wrap;
      }
    }

    if (!caloriesEl) {
      caloriesEl = this.#findCaloriesBlock(this.#getProductSurface());
    }

    if (!priceEl || !titleBlock) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'quick-add-modal__media-meta';
    wrapper.setAttribute('data-quick-add-media-clone', '');

    if (details) {
      wrapper.appendChild(titleBlock.cloneNode(true));
    } else {
      wrapper.appendChild(titleBlock);
    }

    if (caloriesEl) {
      const priceRow = document.createElement('div');
      priceRow.className = 'quick-add-modal__price-row';
      priceRow.appendChild(caloriesEl.cloneNode(true));
      priceRow.appendChild(priceEl.cloneNode(true));
      wrapper.appendChild(priceRow);
    } else {
      wrapper.appendChild(priceEl.cloneNode(true));
    }

    const gallery = media.querySelector('media-gallery');
    if (gallery) {
      gallery.insertAdjacentElement('afterend', wrapper);
    } else {
      media.appendChild(wrapper);
    }
  }

  /**
   * Inserts “View full product information” below buy-buttons (or after the form on mobile).
   * @param {Element} modalContent
   */
  #insertViewFullProductLink(modalContent) {
    const templateEl = document.getElementById('quick-add-view-full-product-template');
    if (!(templateEl instanceof HTMLTemplateElement) || !templateEl.content.firstElementChild) return;

    const buyButtons = modalContent.querySelector('.buy-buttons-block');
    const productForm = modalContent.querySelector('product-form-component');
    const insertAfter = buyButtons ?? productForm;
    if (!insertAfter) return;

    if (insertAfter.nextElementSibling?.classList?.contains('quick-add-modal__view-full-product')) {
      return;
    }

    const fragment = templateEl.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment) || !fragment.firstElementChild) return;
    const root = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const link = root.querySelector('[data-quick-add-view-full-link]');
    if (link instanceof HTMLAnchorElement) {
      link.href = this.productPageUrl;
    }
    insertAfter.insertAdjacentElement('afterend', root);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    if (!anchorElement) return;

    for (const link of this.querySelectorAll('.view-product-title a, .product-header a')) {
      if (link instanceof HTMLAnchorElement) link.href = anchorElement.href;
    }

    const viewFullProductLink = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('[data-quick-add-view-full-link]')
    );
    if (viewFullProductLink) viewFullProductLink.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
